import memoize from "lodash-es/memoize";
import memoizeOne from "memoize-one";
import type { Matrix4x4 } from "mjs";
import { buffers } from "redux-saga";
import { actionChannel, put } from "typed-redux-saga";
import type { OrthoViewRects, Vector3, ViewMode } from "viewer/constants";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { call, select, take } from "viewer/model/sagas/effect_generators";
import type { LoadingStrategy, WebknossosState } from "viewer/store";
import { createWorker } from "viewer/workers/comlink_wrapper";
import type AsyncGetMaximumZoomForAllMags from "../../workers/async_get_maximum_zoom_for_all_mags.worker";
import { getDataLayers, getMagInfo } from "../accessors/dataset_accessor";
import {
  getTransformsForLayer,
  invertAndTranspose,
} from "../accessors/dataset_layer_transformation_accessor";
import { _getDummyFlycamMatrix } from "../accessors/flycam_accessor";
import { getViewportRects } from "../accessors/view_mode_accessor";
import type { Action } from "../actions/actions";
import { setMaximumZoomForAllMagsForLayerAction } from "../actions/flycam_info_cache_actions";
import { getRequiredBucketCapacityPerLayer } from "../bucket_data_handling/data_rendering_logic";
import { ensureWkInitialized } from "./ready_sagas";

const asyncGetMaximumZoomForAllMags = createWorker<typeof AsyncGetMaximumZoomForAllMags>(
  "async_get_maximum_zoom_for_all_mags.worker.ts",
);

const getComputeFunction = memoize((_layerName: string) => {
  // The argument _layerName is not used in this function, but
  // we want to have one memoized function per layer name which
  // is why the argument is still needed.
  return memoizeOne(
    (
      viewMode: ViewMode,
      loadingStrategy: LoadingStrategy,
      voxelSizeFactor: Vector3,
      mags: Array<Vector3>,
      viewportRects: OrthoViewRects,
      maximumCapacity: number,
      layerMatrix: Matrix4x4,
      flycamMatrix: Matrix4x4,
    ) => {
      return asyncGetMaximumZoomForAllMags(
        viewMode,
        loadingStrategy,
        voxelSizeFactor,
        mags,
        viewportRects,
        maximumCapacity,
        layerMatrix,
        flycamMatrix,
      );
    },
  );
});

export default function* maintainMaximumZoomForAllMagsSaga(): Saga<void> {
  // We use an actionChannel so that we don't miss new incoming actions
  // while waiting for the async computation of the last action.
  // We are only interested in the newest action, which is why we use
  // a sliding buffer of size 1.
  // We don't use takeLatest, because that would try to
  // abort a previous calculation (however, the webworker
  // would still complete its computation and the result value
  // can still be useful because the next computation
  // might be able to use the memoization result).
  const channel = yield* actionChannel(
    [
      // These actions *might* affect the values of the parameters
      // that are given to getZoomLevelsFn. If they don't affect the
      // actual value, memoization will avoid recomputation.
      "SET_VIEW_MODE",
      "UPDATE_DATASET_SETTING",
      "UPDATE_USER_SETTING",
      "SET_DATASET",
      "SET_INPUT_CATCHER_RECT",
      "SET_INPUT_CATCHER_RECTS",
      "INITIALIZE_GPU_SETUP",
    ],
    buffers.sliding<Action>(1),
  );

  yield* call(ensureWkInitialized);
  while (true) {
    yield* take(channel);
    const state: WebknossosState = yield* select((state) => state);
    const layers = getDataLayers(state.dataset);

    for (const layer of layers) {
      const layerName = layer.name;

      const getZoomLevelsFn = yield* call(getComputeFunction, layerName);

      const { viewMode } = state.temporaryConfiguration;

      const layerMatrix = invertAndTranspose(
        getTransformsForLayer(
          state.dataset,
          layer,
          state.datasetConfiguration.nativelyRenderedLayerName,
        ).affineMatrix,
      );

      const dummyFlycamMatrix = _getDummyFlycamMatrix(state.dataset.dataSource.scale);

      const zoomLevels = yield* call(
        getZoomLevelsFn,
        viewMode,
        state.datasetConfiguration.loadingStrategy,
        state.dataset.dataSource.scale.factor,
        getMagInfo(layer.mags).getDenseMags(),
        getViewportRects(state),
        Math.min(
          state.temporaryConfiguration.gpuSetup.smallestCommonBucketCapacity,
          getRequiredBucketCapacityPerLayer(
            state.userConfiguration.gpuMemoryFactor,
            state.dataset.dataSource.dataLayers.length,
          ),
        ),
        layerMatrix,
        // Theoretically, the following parameter should be state.flycam.currentMatrix.
        // However, that matrix changes on each move which means that the ranges would need
        // to be recalculate on each move. At least, for orthogonal mode, the actual matrix
        // should only differ in its translation which can be ignored for gauging the maximum
        // zoom here.
        // However, for flight mode this is not really accurate. As a heuristic,
        // this already proved to be fine, though.
        dummyFlycamMatrix,
      );
      if (state.flycamInfoCache.maximumZoomForAllMags[layerName] !== zoomLevels) {
        yield* put(setMaximumZoomForAllMagsForLayerAction(layerName, zoomLevels));
      }
    }
  }
}
